import dayjs from 'dayjs';
import { useCallback, useRef, useState } from 'react';
import {
  Box,
  BoxProps,
  createVarsResolver,
  DataAttributes,
  ElementProps,
  factory,
  Factory,
  getRadius,
  MantineRadius,
  rem,
  ScrollArea,
  ScrollAreaAutosizeProps,
  StylesApiProps,
  UnstyledButton,
  useProps,
  useResolvedStylesApi,
  useStyles,
} from '@mantine/core';
import { useDatesContext } from '@mantine/dates';
import { useIsomorphicEffect, useMergedRef } from '@mantine/hooks';
import { useAutoScrollOnDrag } from '../../hooks/use-auto-scroll-on-drag';
import { useDragDropHandlers } from '../../hooks/use-drag-drop-handlers';
import { useEventResize } from '../../hooks/use-event-resize';
import { useSlotDragSelect } from '../../hooks/use-slot-drag-select';
import { getLabel, ScheduleLabelsOverride } from '../../labels';
import {
  AnyDateValue,
  DateLabelFormat,
  DateStringValue,
  DateTimeStringValue,
  PreventEventOverlap,
  ScheduleCanDropEventData,
  ScheduleCanDropExternalEventData,
  ScheduleCanResizeEventToData,
  ScheduleEventData,
  ScheduleEventOverlapMode,
  ScheduleEventPlacementRejectedData,
  ScheduleMode,
  ScheduleViewLevel,
} from '../../types';
import {
  calculateDropTime,
  clampIntervalMinutes,
  expandRecurringEvents,
  formatDate,
  addMinutesToTime,
  getBusinessHoursState,
  getDayPosition,
  getDayTimeIntervals,
  getSlotLabelGroups,
  getTimeAxisEventStyle,
  getVisibleEvents,
  isAllDayEvent,
  toDateString,
} from '../../utils';
import { getSlotLabelDate } from '../../utils/get-slot-label-date/get-slot-label-date';
import { AgendaView, AgendaViewStylesNames } from '../AgendaView/AgendaView';
import {
  CurrentTimeIndicator,
  CurrentTimeIndicatorStylesNames,
} from '../CurrentTimeIndicator/CurrentTimeIndicator';
import { DragContext } from '../DragContext/DragContext';
import { MoreEvents, MoreEventsProps, MoreEventsStylesNames } from '../MoreEvents/MoreEvents';
import { ScheduleBackgroundEvent } from '../ScheduleBackgroundEvent';
import {
  RenderEvent,
  RenderEventBody,
  ScheduleEvent,
  ScheduleEventStylesNames,
} from '../ScheduleEvent/ScheduleEvent';
import { MonthYearSelectStylesNames } from '../ScheduleHeader/MonthYearSelect/MonthYearSelect';
import { CombinedScheduleHeaderStylesNames } from '../ScheduleHeader/ScheduleHeader';
import { ScheduleHeaderBase } from '../ScheduleHeader/ScheduleHeaderBase';
import { ViewSelectProps } from '../ScheduleHeader/ViewSelect/ViewSelect';
import { getDayViewEvents } from './get-day-view-events/get-day-view-events';
import classes from './DayView.module.css';

export type DayViewStylesNames =
  | 'dayView'
  | 'dayViewInner'
  | 'dayViewScrollArea'
  | 'dayViewAllDay'
  | 'dayViewAllDayEvents'
  | 'dayViewSlot'
  | 'dayViewSlots'
  | 'dayViewTimeSlots'
  | 'dayViewSlotLabel'
  | 'dayViewSlotLabels'
  | 'dayViewBackgroundEvent'
  | 'dayViewBackgroundEventResizeHandle'
  | 'dayViewDragPreview'
  | MoreEventsStylesNames
  | ScheduleEventStylesNames
  | Exclude<CombinedScheduleHeaderStylesNames, MonthYearSelectStylesNames>
  | CurrentTimeIndicatorStylesNames
  | AgendaViewStylesNames;

export type DayViewCssVariables = {
  dayView:
    | '--day-view-radius'
    | '--day-view-slot-height'
    | '--day-view-all-day-slot-height'
    | '--day-view-business-hours-bg'
    | '--day-view-non-business-hours-bg';
};

export interface DayViewProps
  extends BoxProps, StylesApiProps<DayViewFactory>, ElementProps<'div'> {
  __staticSelector?: string;

  /** Day to display, Date object or date string in `YYYY-MM-DD` format */
  date: Date | DateStringValue;

  /** Called when date is changed */
  onDateChange?: (date: DateStringValue) => void;

  /** Events to display, must be a stable reference */
  events?: ScheduleEventData[];

  /** Time slots start time, in `HH:mm:ss` format @default 00:00:00 */
  startTime?: string;

  /** Time slots end time in `HH:mm:ss` format @default 23:59:59 */
  endTime?: string;

  /** Number of minutes for each time slot. Must divide evenly into an hour (e.g. `15`, `30`) or be a whole number of hours (e.g. `120`, `240`) @default 15 */
  intervalMinutes?: number;

  /** If set, grid lines are displayed for intervals smaller than one hour, for example 15 and 30 minutes intervals @default true */
  withSubHourGridLines?: boolean;

  /** Determines how events that overlap in time are laid out: `columns` splits the available width between them, `cascade` indents each event and stacks it over the previous one @default 'columns' */
  eventOverlapMode?: ScheduleEventOverlapMode;

  /** Time in ms the pointer must rest on an event before it is raised above the events covering it, only used with `eventOverlapMode="cascade"` @default 600 */
  eventOverlapRaiseDelay?: number;

  /** If set, the all-day slot is displayed below the header @default true */
  withAllDaySlot?: boolean;

  /** Locale passed down to dayjs, overrides value defined on `DatesProvider` */
  locale?: string;

  /** Key of `theme.radius` or any valid CSS value to set `border-radius` @default theme.defaultRadius */
  radius?: MantineRadius;

  /** Dayjs format for slot labels or a callback function that returns formatted value @default HH:mm  */
  slotLabelFormat?: DateLabelFormat;

  /** Interval between time labels in minutes. Must be a multiple of `intervalMinutes` that divides evenly into an hour (e.g. `15`, `30`) or is a whole number of hours. Values that do not satisfy these rules are rounded up to the nearest valid value, `60` is used for invalid values. @default 60 */
  slotLabelInterval?: number;

  /** Date format in the header @default MMMM D, YYYY */
  headerFormat?: DateLabelFormat;

  /** If set, displays a line indicating the current time. By default, displayed only for the current day. */
  withCurrentTimeIndicator?: boolean;

  /** If set, the time indicator displays the current time in the bubble @default true */
  withCurrentTimeBubble?: boolean;

  /** A function to get the current time, called on every tick. Can be used to display the current time indicator in a different timezone. @default () => dayjs() */
  getCurrentTime?: () => AnyDateValue;

  /** If set, the header is displayed @default true */
  withHeader?: boolean;

  /** Called when view level select button is clicked */
  onViewChange?: (view: ScheduleViewLevel) => void;

  /** Props passed to previous month control */
  previousControlProps?: React.ComponentProps<'button'> & DataAttributes;

  /** Props passed to next month control */
  nextControlProps?: React.ComponentProps<'button'> & DataAttributes;

  /** Props passed to today control */
  todayControlProps?: React.ComponentProps<'button'> & DataAttributes;

  /** Props passed to view level select */
  viewSelectProps?: Partial<ViewSelectProps> & DataAttributes;

  /** Height of 1hr slot @default 64px */
  slotHeight?: React.CSSProperties['height'];

  /** Height of all-day slot @default 44px */
  allDaySlotHeight?: React.CSSProperties['height'];

  /** Props passed down to the `ScrollArea.Autosize` component */
  scrollAreaProps?: ScrollAreaAutosizeProps & DataAttributes;

  /** Props passed down to `MoreEvents` component */
  moreEventsProps?: Partial<MoreEventsProps>;

  /** Function to customize event body, `event` object is passed as first argument */
  renderEventBody?: RenderEventBody;

  /** Function to fully customize event rendering, receives all props that would be passed to the root element including children */
  renderEvent?: RenderEvent;

  /** Labels override */
  labels?: ScheduleLabelsOverride;

  /** If set to true, highlights business hours with white background @default false */
  highlightBusinessHours?: boolean;

  /** Business hours range in `HH:mm:ss` format @default ['09:00:00', '17:00:00'] */
  businessHours?: [string, string];

  /** If true, events can be dragged and dropped @default false */
  withEventsDragAndDrop?: boolean;

  /** Called when event is dropped at new time */
  onEventDrop?: (data: {
    eventId: string | number;
    newStart: DateTimeStringValue;
    newEnd: DateTimeStringValue;
    event: ScheduleEventData;
  }) => void;

  /** Function to determine if event can be dragged */
  canDragEvent?: (event: ScheduleEventData) => boolean;

  /** Snap step for moving events by drag and drop, in minutes. Must divide evenly into an hour (e.g. `15`, `30`) or be a whole number of hours. When not set, `intervalMinutes` is used. @default intervalMinutes */
  eventDragInterval?: number;

  /** Called when any event drag starts */
  onEventDragStart?: (event: ScheduleEventData) => void;

  /** Called when any event drag ends */
  onEventDragEnd?: () => void;

  /** Called when time slot is clicked */
  onTimeSlotClick?: (data: {
    slotStart: DateTimeStringValue;
    slotEnd: DateTimeStringValue;
    nativeEvent: React.MouseEvent<HTMLButtonElement>;
  }) => void;

  /** Called when all-day slot is clicked */
  onAllDaySlotClick?: (date: DateStringValue, event: React.MouseEvent<HTMLButtonElement>) => void;

  /** Called when event is clicked */
  onEventClick?: (event: ScheduleEventData, e: React.MouseEvent<HTMLButtonElement>) => void;

  /** If set, background events (`display: 'background'`) can be clicked and trigger `onEventClick`. Combined with `withEventResize`, timed background events can also be resized by dragging their edges. @default false */
  withInteractiveBackgroundEvents?: boolean;

  /** If set, enables drag-to-select time slot ranges @default false */
  withDragSlotSelect?: boolean;

  /** Called when a time slot range is selected by dragging */
  onSlotDragEnd?: (rangeStart: DateTimeStringValue, rangeEnd: DateTimeStringValue) => void;

  /** Interaction mode: 'default' allows all interactions, 'static' disables event interactions @default default */
  mode?: ScheduleMode;

  /** Time to scroll to on initial render, in `HH:mm:ss` format */
  startScrollTime?: string;

  /** Called when an external item is dropped onto the schedule. Receives the `DataTransfer` object and the drop target datetime. */
  onExternalEventDrop?: (dataTransfer: DataTransfer, dropDateTime: DateTimeStringValue) => void;

  /** If true, events can be resized by dragging their edges @default false */
  withEventResize?: boolean;

  /** Called when event is resized */
  onEventResize?: (data: {
    eventId: string | number;
    newStart: DateTimeStringValue;
    newEnd: DateTimeStringValue;
    event: ScheduleEventData;
  }) => void;

  /** Function to determine if event can be resized */
  canResizeEvent?: (event: ScheduleEventData) => boolean;

  /** Called while an event is dragged over a target to compute live feedback and again before the drop is committed, return `false` to reject the drop. Must be pure and cheap. */
  canDropEvent?: (data: ScheduleCanDropEventData) => boolean;

  /** Called while an external item is dragged over a target to compute live feedback and again before the drop is committed, return `false` to reject the drop. Must be pure and cheap. Only `dataTransfer.types` can be read while the drag is in progress. */
  canDropExternalEvent?: (data: ScheduleCanDropExternalEventData) => boolean;

  /** Called on every pointer move while an event is resized to compute live feedback and again before the resize is committed, return `false` to reject the new size. Must be pure and cheap. */
  canResizeEventTo?: (data: ScheduleCanResizeEventToData) => boolean;

  /** If set, drops and resizes that would make the event overlap another event are rejected. Pass a function to decide per pair of events: return `true` to forbid the overlap. The function runs while dragging and resizing to compute live feedback, it must be pure and cheap. @default false */
  preventEventOverlap?: PreventEventOverlap;

  /** Called when a drop or resize is rejected */
  onEventPlacementRejected?: (data: ScheduleEventPlacementRejectedData) => void;

  /** Snap step for resizing events, in minutes. Must divide evenly into an hour (e.g. `15`, `30`) or be a whole number of hours. When not set, `intervalMinutes` is used. @default intervalMinutes */
  eventResizeInterval?: number;

  /** Max number of generated recurring instances per recurring series @default 2000 */
  recurrenceExpansionLimit?: number;

  /** Function to get additional props for each time slot. Receives slot start and end datetime strings. Returned props are spread onto the slot element. Event handlers returned by this function are composed with internal handlers (e.g. onClick) rather than overriding them. */
  getTimeSlotProps?: (data: {
    start: DateTimeStringValue;
    end: DateTimeStringValue;
  }) => Record<string, any> | undefined;

  /** If set, displays an Agenda button in the header that opens an agenda list view @default false */
  withAgenda?: boolean;
}

export type DayViewFactory = Factory<{
  props: DayViewProps;
  ref: HTMLDivElement;
  stylesNames: DayViewStylesNames;
  vars: DayViewCssVariables;
}>;

const defaultProps = {
  __staticSelector: 'DayView',
  startTime: '00:00:00',
  endTime: '23:59:59',
  intervalMinutes: 15,
  withSubHourGridLines: true,
  withAllDaySlot: true,
  slotLabelFormat: 'HH:mm',
  slotLabelInterval: 60,
  headerFormat: 'MMMM D, YYYY',
  withCurrentTimeBubble: true,
  withHeader: true,
  highlightBusinessHours: false,
  businessHours: ['09:00:00', '17:00:00'],
  withEventsDragAndDrop: false,
  withDragSlotSelect: false,
  withEventResize: false,
  mode: 'default',
  withInteractiveBackgroundEvents: false,
  eventOverlapMode: 'columns',
  eventOverlapRaiseDelay: 600,
} satisfies Partial<DayViewProps>;

const varsResolver = createVarsResolver<DayViewFactory>(
  (_theme, { radius, slotHeight, allDaySlotHeight }) => ({
    dayView: {
      '--day-view-radius': radius !== undefined ? getRadius(radius) : undefined,
      '--day-view-slot-height': rem(slotHeight),
      '--day-view-all-day-slot-height': rem(allDaySlotHeight),
      '--day-view-business-hours-bg': undefined,
      '--day-view-non-business-hours-bg': undefined,
    },
  })
);

export const DayView = factory<DayViewFactory>((_props) => {
  const props = useProps('DayView', defaultProps, _props);
  const {
    classNames,
    className,
    style,
    styles,
    unstyled,
    attributes,
    vars,
    __staticSelector,
    startTime,
    endTime,
    intervalMinutes,
    withSubHourGridLines,
    eventOverlapMode,
    eventOverlapRaiseDelay,
    withAllDaySlot,
    date,
    locale,
    slotLabelFormat,
    slotLabelInterval,
    headerFormat,
    getCurrentTime,
    withCurrentTimeIndicator,
    withCurrentTimeBubble,
    withHeader,
    radius,
    onDateChange,
    onViewChange,
    previousControlProps,
    nextControlProps,
    todayControlProps,
    viewSelectProps,
    slotHeight,
    allDaySlotHeight,
    scrollAreaProps,
    events,
    moreEventsProps,
    renderEventBody,
    renderEvent,
    labels,
    highlightBusinessHours,
    businessHours,
    withEventsDragAndDrop,
    onEventDrop,
    canDragEvent,
    eventDragInterval,
    onEventDragStart,
    onEventDragEnd,
    onTimeSlotClick,
    onAllDaySlotClick,
    onEventClick,
    withInteractiveBackgroundEvents,
    withDragSlotSelect,
    onSlotDragEnd,
    mode,
    startScrollTime,
    onExternalEventDrop,
    withEventResize,
    onEventResize,
    canResizeEvent,
    canDropEvent,
    canDropExternalEvent,
    canResizeEventTo,
    preventEventOverlap,
    onEventPlacementRejected,
    eventResizeInterval,
    recurrenceExpansionLimit,
    getTimeSlotProps,
    withAgenda,
    ...others
  } = props;

  const [agendaOpen, setAgendaOpen] = useState(false);

  const getStyles = useStyles<DayViewFactory>({
    name: __staticSelector,
    classes,
    props,
    className,
    style,
    classNames,
    styles,
    unstyled,
    attributes,
    vars,
    varsResolver,
    rootSelector: 'dayView',
  });

  const { resolvedClassNames, resolvedStyles } = useResolvedStylesApi<DayViewFactory>({
    classNames,
    styles,
    props,
  });

  const stylesApiProps = {
    classNames: resolvedClassNames,
    styles: resolvedStyles,
    attributes,
    __staticSelector,
    radius,
  };

  const ctx = useDatesContext();
  const resolveNow = () => (getCurrentTime ? dayjs(getCurrentTime()) : dayjs());
  const showCurrentTimeIndicator =
    withCurrentTimeIndicator ?? dayjs(date).isSame(resolveNow(), 'day');
  const slots = getDayTimeIntervals({
    startTime,
    endTime,
    intervalMinutes,
    labelIntervalMinutes: slotLabelInterval,
  });
  const slotsRef = useRef<HTMLButtonElement[]>([]);
  const viewportRef = useRef<HTMLDivElement>(null);
  const timeSlotsContainerRef = useRef<HTMLDivElement>(null);
  const mergedViewportRef = useMergedRef(viewportRef, scrollAreaProps?.viewportRef);

  useAutoScrollOnDrag({
    viewportRef,
    enabled: (withEventsDragAndDrop || !!onExternalEventDrop) && mode !== 'static',
  });

  useIsomorphicEffect(() => {
    if (!startScrollTime || !viewportRef.current || slotsRef.current.length === 0) {
      return;
    }

    const targetIndex = slots.findIndex((s) => s.startTime >= startScrollTime);
    if (targetIndex < 0) {
      return;
    }

    const targetSlot = slotsRef.current[targetIndex];
    if (!targetSlot) {
      return;
    }

    const slotRect = targetSlot.getBoundingClientRect();
    const viewportRect = viewportRef.current.getBoundingClientRect();
    viewportRef.current.scrollTo({
      left: 0,
      top: slotRect.top - viewportRect.top + viewportRef.current.scrollTop,
    });
  }, []);

  const dragOffsetRef = useRef<{ offset: number; size: number }>({ offset: 0, size: 0 });

  const getSlotIndexFromDragPoint = useCallback((event: React.DragEvent) => {
    const slotIndex = slotsRef.current.findIndex((slotNode) => {
      if (!slotNode) {
        return false;
      }

      const rect = slotNode.getBoundingClientRect();
      return event.clientY >= rect.top && event.clientY <= rect.bottom;
    });

    if (slotIndex >= 0) {
      const rect = slotsRef.current[slotIndex]!.getBoundingClientRect();
      dragOffsetRef.current = { offset: event.clientY - rect.top, size: rect.height };
      return slotIndex;
    }

    const firstSlot = slotsRef.current[0];
    const lastSlot = slotsRef.current[slotsRef.current.length - 1];

    if (!firstSlot || !lastSlot) {
      return null;
    }

    const firstRect = firstSlot.getBoundingClientRect();
    const lastRect = lastSlot.getBoundingClientRect();

    if (event.clientY < firstRect.top) {
      dragOffsetRef.current = { offset: 0, size: firstRect.height };
      return 0;
    }

    if (event.clientY > lastRect.bottom) {
      dragOffsetRef.current = { offset: lastRect.height, size: lastRect.height };
      return slotsRef.current.length - 1;
    }

    return null;
  }, []);

  const handleSlotKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, slotIndex: number) => {
    const direction = event.key;
    if (direction === 'ArrowDown' && slotIndex < slots.length - 1) {
      event.preventDefault();
      slotsRef.current[slotIndex + 1]?.focus();
    } else if (direction === 'ArrowUp' && slotIndex > 0) {
      event.preventDefault();
      slotsRef.current[slotIndex - 1]?.focus();
    }
  };

  const slotDragSelect = useSlotDragSelect({
    enabled: withDragSlotSelect && mode !== 'static',
    onDragEnd: (startIndex, endIndex) => {
      if (!onSlotDragEnd) {
        return;
      }
      const slotDate = dayjs(date).format('YYYY-MM-DD');
      onSlotDragEnd(
        `${slotDate} ${slots[startIndex].startTime}`,
        `${slotDate} ${slots[endIndex].endTime}`
      );
    },
  });

  const expandedEvents = expandRecurringEvents({
    events,
    rangeStart: dayjs(date).startOf('day').toDate(),
    rangeEnd: dayjs(date).endOf('day').toDate(),
    expansionLimit: recurrenceExpansionLimit,
  });

  const eventsData = getDayViewEvents({
    events: expandedEvents,
    date,
    startTime,
    endTime,
    intervalMinutes,
    eventOverlapMode,
  });

  const getExternalDropDateTime = useCallback(
    (slotIndex: number) => {
      const slotDate = dayjs(date).format('YYYY-MM-DD');
      const slotTime = slots[slotIndex].startTime;
      const slotStart = `${slotDate} ${slotTime}`;

      if (eventDragInterval == null) {
        return slotStart;
      }

      const { start } = calculateDropTime({
        draggedEvent: { start: slotStart, end: slotStart } as ScheduleEventData,
        targetDate: date,
        targetSlotTime: slotTime,
        intervalMinutes,
        dragIntervalMinutes: eventDragInterval,
        slotOffset: dragOffsetRef.current.offset,
        slotSize: dragOffsetRef.current.size,
        startTime,
        endTime,
      });

      return dayjs(start).format('YYYY-MM-DD HH:mm:ss');
    },
    [date, slots, eventDragInterval, intervalMinutes, startTime, endTime]
  );

  const handleExternalDrop = useCallback(
    (e: React.DragEvent, slotIndex: number) => {
      if (!onExternalEventDrop) {
        return;
      }
      onExternalEventDrop(e.dataTransfer, getExternalDropDateTime(slotIndex));
    },
    [onExternalEventDrop, getExternalDropDateTime]
  );

  const getDropTimeForSlot = (slotIndex: number, draggedEvent: ScheduleEventData) => {
    const slotTime = slots[slotIndex].startTime;
    return calculateDropTime({
      draggedEvent,
      targetDate: date,
      targetSlotTime: slotTime,
      intervalMinutes,
      dragIntervalMinutes: eventDragInterval,
      slotOffset: eventDragInterval == null ? undefined : dragOffsetRef.current.offset,
      slotSize: eventDragInterval == null ? undefined : dragOffsetRef.current.size,
      startTime,
      endTime,
    });
  };

  const dragDrop = useDragDropHandlers({
    enabled: withEventsDragAndDrop,
    mode,
    onEventDrop,
    canDragEvent,
    onEventDragStart,
    onEventDragEnd,
    calculateDropTarget: getDropTimeForSlot,
    onExternalDrop: onExternalEventDrop ? handleExternalDrop : undefined,
    events: expandedEvents,
    preventEventOverlap,
    canDropEvent,
    canDropExternalEvent,
    onEventPlacementRejected,
    getExternalDropDateTime,
  });

  const updateDragPreview = (slotIndex: number) => {
    const draggedEvent = dragDrop.dragContextValue.draggedEvent;
    if (eventDragInterval == null || !draggedEvent) {
      return;
    }
    const { start, end } = getDropTimeForSlot(slotIndex, draggedEvent);
    const newStart = dayjs(start).format('YYYY-MM-DD HH:mm:ss');
    if (dragDrop.dragPreview?.start !== newStart) {
      dragDrop.setDragPreview({
        start: newStart,
        end: dayjs(end).format('YYYY-MM-DD HH:mm:ss'),
        target: slotIndex,
      });
    }
  };

  const eventResize = useEventResize({
    enabled: withEventResize,
    mode,
    startTime,
    endTime,
    intervalMinutes,
    resizeIntervalMinutes: eventResizeInterval,
    onEventResize,
    canResizeEvent,
    events: expandedEvents,
    preventEventOverlap,
    canResizeEventTo,
    onEventPlacementRejected,
    withBackgroundEvents: withInteractiveBackgroundEvents,
  });

  const withDragHandlers = (withEventsDragAndDrop || !!onExternalEventDrop) && mode !== 'static';

  const eventsNodes = eventsData.regularEvents.map((event) => {
    const eventIsAllDay = isAllDayEvent({ event, date });
    const isDraggable = !eventIsAllDay && dragDrop.isDraggableEvent(event);
    const isResizable = !eventIsAllDay && eventResize.isResizableEvent(event);
    const resizePosition = eventResize.getResizePosition(event.id);
    const eventTop = resizePosition ? resizePosition.top : event.position.top;
    const eventHeight = resizePosition ? resizePosition.height : event.position.height;

    return (
      <ScheduleEvent
        event={event}
        key={event.id}
        renderEventBody={renderEventBody}
        renderEvent={renderEvent}
        autoSize
        draggable={isDraggable}
        withResize={isResizable}
        isResizing={resizePosition !== null}
        onResizeStart={
          isResizable
            ? (edge, e) => {
                if (timeSlotsContainerRef.current) {
                  eventResize.handleResizeStart({
                    event,
                    edge,
                    container: timeSlotsContainerRef.current,
                    originalTop: event.position.top,
                    originalHeight: event.position.height,
                    eventDate: dayjs(date).format('YYYY-MM-DD'),
                    pointerEvent: e,
                  });
                }
              }
            : undefined
        }
        mode={mode}
        onClick={
          onEventClick
            ? (e) => {
                if (!eventResize.wasResizing()) {
                  onEventClick(event, e);
                }
              }
            : undefined
        }
        {...stylesApiProps}
        mod={{
          cascade: eventOverlapMode === 'cascade',
          invalid: resizePosition !== null && !eventResize.resizeValid,
        }}
        style={{
          ...stylesApiProps.styles?.event,
          top: `${eventTop}%`,
          height: `${eventHeight}%`,
          insetInlineStart: `${event.position.offset}%`,
          width: `${event.position.width}%`,
          position: 'absolute',
          '--event-z-index': event.position.column + 3,
          '--event-z-index-raised': event.position.overlaps + 3,
        }}
      />
    );
  });

  const allDayEventsCount = getVisibleEvents({
    maxEvents: 2,
    totalEvents: eventsData.allDayEvents.length,
  });

  const allDayEventsNodes = eventsData.allDayEvents
    .slice(0, allDayEventsCount.visibleEventsCount)
    .map((event) => (
      <ScheduleEvent
        key={event.id}
        event={event}
        renderEventBody={renderEventBody}
        renderEvent={renderEvent}
        style={{ maxHeight: '50%' }}
        nowrap
        autoSize
        mode={mode}
        onClick={onEventClick ? (e) => onEventClick(event, e) : undefined}
        {...stylesApiProps}
      />
    ));

  const dayGroup = dayjs(date).format('YYYY-MM-DD');

  const suppressDropHighlight = eventDragInterval != null && dragDrop.dragContextValue.isDragging;

  const items = slots.map((slot, index) => {
    const isDropTarget = !suppressDropHighlight && dragDrop.isDropTarget(index);
    const isDragSelected = slotDragSelect.isSlotSelected(index, dayGroup);
    const slotStart = `${dayGroup} ${slot.startTime}` as DateTimeStringValue;
    const slotEnd = `${dayGroup} ${slot.endTime}` as DateTimeStringValue;
    const { onClick: externalOnClick, ...externalSlotProps } =
      getTimeSlotProps?.({ start: slotStart, end: slotEnd }) || {};

    const handleClick =
      mode === 'static'
        ? undefined
        : (event: React.MouseEvent<HTMLButtonElement>) => {
            onTimeSlotClick?.({
              slotStart,
              slotEnd,
              nativeEvent: event,
            });
            externalOnClick?.(event);
          };

    const businessHoursState = getBusinessHoursState({
      time: slot.startTime,
      endTime: addMinutesToTime(slot.startTime, clampIntervalMinutes(intervalMinutes)),
      businessHours,
      highlightBusinessHours,
    });

    return (
      <UnstyledButton
        {...getStyles('dayViewSlot')}
        key={slot.startTime}
        ref={(node) => {
          slotsRef.current[index] = node!;
        }}
        mod={{
          first: index === 0,
          'hour-start': slot.isHourStart,
          ...businessHoursState.mod,
          'drop-target': isDropTarget,
          'drag-selected': isDragSelected,
          static: mode === 'static',
        }}
        __vars={{
          '--slot-size': `${clampIntervalMinutes(intervalMinutes) / 60}`,
          ...businessHoursState.vars,
        }}
        aria-label={`${getLabel('timeSlot', labels)} ${slot.startTime} - ${slot.endTime}`}
        tabIndex={mode === 'static' ? -1 : index === 0 ? 0 : -1}
        data-drag-slot-index={withDragSlotSelect && mode !== 'static' ? index : undefined}
        data-drag-slot-group={withDragSlotSelect && mode !== 'static' ? dayGroup : undefined}
        onKeyDown={mode === 'static' ? undefined : (e) => handleSlotKeyDown(e, index)}
        onPointerDown={
          withDragSlotSelect && mode !== 'static'
            ? (e) => slotDragSelect.handleSlotPointerDown(e, index, dayGroup)
            : undefined
        }
        onClick={handleClick}
        onDragOver={withDragHandlers ? (event) => event.preventDefault() : undefined}
        {...externalSlotProps}
      />
    );
  });

  const slotsLabels = getSlotLabelGroups(slots).map((group) => {
    const label = group.isLabelStart
      ? formatDate({
          locale: ctx.getLocale(locale),
          date: getSlotLabelDate(date, group.startTime),
          format: slotLabelFormat,
        })
      : null;

    const labelBusinessHours = getBusinessHoursState({
      time: group.startTime,
      endTime: group.endTime,
      businessHours,
      highlightBusinessHours,
    });

    return (
      <Box
        {...getStyles('dayViewSlotLabel')}
        key={group.startTime}
        mod={{ 'sub-hour': !group.isHourStart, ...labelBusinessHours.mod }}
        __vars={{
          ...labelBusinessHours.vars,
          '--slot-label-size': `${(group.slotsCount * clampIntervalMinutes(intervalMinutes)) / 60}`,
        }}
      >
        {label}
      </Box>
    );
  });

  const interactiveBackgroundEvents = withInteractiveBackgroundEvents && mode !== 'static';

  const backgroundAllDayEventNodes = eventsData.backgroundAllDayEvents.map((event) => (
    <ScheduleBackgroundEvent
      key={`bg-allday-${event.id}`}
      event={event}
      renderEvent={renderEvent}
      renderEventBody={renderEventBody}
      interactive={interactiveBackgroundEvents}
      onEventClick={onEventClick}
      {...getStyles('dayViewBackgroundEvent', {
        style: { top: 0, height: '100%', width: '100%' },
      })}
    />
  ));

  const backgroundTimedEventNodes = eventsData.backgroundTimedEvents.map((event) => {
    const isResizable = eventResize.isResizableEvent(event);
    const resizePosition = eventResize.getResizePosition(event.id);

    return (
      <ScheduleBackgroundEvent<'top' | 'bottom'>
        key={event.id}
        event={event}
        renderEvent={renderEvent}
        renderEventBody={renderEventBody}
        interactive={interactiveBackgroundEvents}
        onEventClick={
          onEventClick
            ? (clickedEvent, e) => {
                if (!eventResize.wasResizing()) {
                  onEventClick(clickedEvent, e);
                }
              }
            : undefined
        }
        withResize={isResizable}
        isResizing={resizePosition !== null}
        resizeHandleProps={getStyles('dayViewBackgroundEventResizeHandle')}
        onResizeStart={
          isResizable
            ? (edge, e) => {
                if (timeSlotsContainerRef.current) {
                  eventResize.handleResizeStart({
                    event,
                    edge,
                    container: timeSlotsContainerRef.current,
                    originalTop: event.position.top,
                    originalHeight: event.position.height,
                    eventDate: dayjs(date).format('YYYY-MM-DD'),
                    pointerEvent: e,
                  });
                }
              }
            : undefined
        }
        {...getStyles('dayViewBackgroundEvent', {
          style: {
            ...getTimeAxisEventStyle({
              start: resizePosition ? resizePosition.top : event.position.top,
              span: resizePosition ? resizePosition.height : event.position.height,
              axis: 'vertical',
            }),
            width: '100%',
          },
        })}
      />
    );
  });

  const content = (
    <Box
      {...getStyles('dayView')}
      __vars={{ '--event-raise-delay': `${eventOverlapRaiseDelay}ms` }}
      mod={{
        static: mode === 'static',
        'slot-dragging': slotDragSelect.isDragging,
        'hide-sub-hour-grid-lines': !withSubHourGridLines,
        'event-interaction': eventResize.isResizing || dragDrop.dragContextValue.isDragging,
      }}
      {...others}
    >
      {withHeader && (
        <ScheduleHeaderBase
          view="day"
          navigationHandlers={{
            previous: () => toDateString(dayjs(date).subtract(1, 'day')),
            next: () => toDateString(dayjs(date).add(1, 'day')),
            today: () => toDateString(resolveNow()),
          }}
          control={{
            title: formatDate({ locale: ctx.getLocale(locale), date, format: headerFormat }),
            miw: 140,
          }}
          labels={labels}
          onDateChange={onDateChange}
          onViewChange={onViewChange}
          previousControlProps={previousControlProps}
          nextControlProps={nextControlProps}
          todayControlProps={todayControlProps}
          viewSelectProps={viewSelectProps}
          stylesApiProps={stylesApiProps}
          onAgendaClick={withAgenda ? () => setAgendaOpen((v) => !v) : undefined}
          agendaActive={agendaOpen}
        />
      )}

      {agendaOpen && (
        <AgendaView
          rangeStart={dayjs(date).startOf('day').format('YYYY-MM-DD')}
          rangeEnd={dayjs(date).endOf('day').format('YYYY-MM-DD')}
          events={events}
          locale={locale}
          labels={labels}
          mode={mode}
          onEventClick={onEventClick}
          recurrenceExpansionLimit={recurrenceExpansionLimit}
          {...stylesApiProps}
        />
      )}

      {!agendaOpen && (
        <ScrollArea.Autosize
          scrollbarSize={4}
          {...scrollAreaProps}
          {...getStyles('dayViewScrollArea', {
            className: scrollAreaProps?.className,
            style: scrollAreaProps?.style,
          })}
          viewportRef={mergedViewportRef}
        >
          <Box {...getStyles('dayViewInner')}>
            <div {...getStyles('dayViewSlotLabels')}>
              {withAllDaySlot && (
                <Box {...getStyles('dayViewSlotLabel')} mod={{ 'all-day': true }}>
                  {getLabel('allDay', labels)}
                </Box>
              )}
              {slotsLabels}
            </div>

            <div {...getStyles('dayViewSlots')}>
              {withAllDaySlot && (
                <div {...getStyles('dayViewAllDay')}>
                  {backgroundAllDayEventNodes}
                  <div {...getStyles('dayViewAllDayEvents')}>
                    {allDayEventsNodes}
                    {allDayEventsCount.hiddenEventsCount > 0 && (
                      <MoreEvents
                        events={eventsData.allDayEvents}
                        moreEventsCount={allDayEventsCount.hiddenEventsCount}
                        renderEventBody={renderEventBody}
                        renderEvent={renderEvent}
                        mode={mode}
                        labels={labels}
                        onEventClick={onEventClick}
                        {...stylesApiProps}
                        {...moreEventsProps}
                      />
                    )}
                  </div>
                  <UnstyledButton
                    {...getStyles('dayViewSlot')}
                    mod={{ 'all-day': true, static: mode === 'static' }}
                    aria-label={`${getLabel('timeSlot', labels)} ${getLabel('allDay', labels)}`}
                    onClick={
                      mode === 'static' || !onAllDaySlotClick
                        ? undefined
                        : (e) => {
                            onAllDaySlotClick(dayjs(date).format('YYYY-MM-DD'), e);
                          }
                    }
                  />
                </div>
              )}

              <div
                ref={timeSlotsContainerRef}
                {...getStyles('dayViewTimeSlots')}
                onDragOver={
                  withDragHandlers
                    ? (event) => {
                        const slotIndex = getSlotIndexFromDragPoint(event);
                        if (slotIndex !== null) {
                          dragDrop.handleDragOver(event, slotIndex);
                          updateDragPreview(slotIndex);
                        }
                      }
                    : undefined
                }
                onDragLeave={withDragHandlers ? dragDrop.handleDragLeave : undefined}
                onDrop={
                  withDragHandlers
                    ? (event) => {
                        const slotIndex = getSlotIndexFromDragPoint(event);
                        if (slotIndex !== null) {
                          dragDrop.handleDrop(event, slotIndex);
                        }
                      }
                    : undefined
                }
              >
                {backgroundTimedEventNodes}

                {eventsNodes}

                {dragDrop.dragPreview && dragDrop.dragContextValue.draggedEvent && (
                  <Box
                    mod={{ invalid: !dragDrop.dropValid }}
                    {...getStyles('dayViewDragPreview', {
                      style: (() => {
                        const { top, height } = getDayPosition({
                          event: {
                            ...dragDrop.dragContextValue.draggedEvent,
                            start: dragDrop.dragPreview.start,
                            end: dragDrop.dragPreview.end,
                          },
                          startTime,
                          endTime,
                          intervalMinutes,
                        });
                        return {
                          position: 'absolute',
                          insetInlineStart: 0,
                          insetInlineEnd: 0,
                          top: `${top}%`,
                          height: `${height}%`,
                        };
                      })(),
                    })}
                  />
                )}

                {showCurrentTimeIndicator && (
                  <CurrentTimeIndicator
                    startOffset="calc(var(--day-view-slot-labels-width) * -1)"
                    endOffset="0rem"
                    topOffset="0rem"
                    timeBubbleStartOffset="calc(var(--day-view-slot-labels-width) * -1 + 30px)"
                    currentTimeFormat={slotLabelFormat}
                    withTimeBubble={withCurrentTimeBubble}
                    withThumb={!withCurrentTimeBubble}
                    locale={locale}
                    startTime={startTime}
                    endTime={endTime}
                    intervalMinutes={intervalMinutes}
                    getCurrentTime={getCurrentTime}
                    {...stylesApiProps}
                  />
                )}
                {items}
              </div>
            </div>
          </Box>
        </ScrollArea.Autosize>
      )}
    </Box>
  );

  if (withEventsDragAndDrop) {
    return <DragContext.Provider value={dragDrop.dragContextValue}>{content}</DragContext.Provider>;
  }

  return content;
});

DayView.displayName = '@mantine/schedule/DayView';
DayView.classes = classes;
DayView.varsResolver = varsResolver;

export namespace DayView {
  export type Props = DayViewProps;
  export type Factory = DayViewFactory;
  export type StylesNames = DayViewStylesNames;
  export type CssVariables = DayViewCssVariables;
}
